import { plotByKind } from './registry';
import { clampTimestamp } from './time';
import type { PanelNavigation, PlotSpec, WorkspaceState } from './types';

const SCANNABLE_DOMAINS = new Set(['day', 'snapshot', 'relative']);

export function isScannable(plot: PlotSpec): boolean {
  return SCANNABLE_DOMAINS.has(plotByKind(plot.kind).timeDomain);
}

export function firstScanLeader(plots: PlotSpec[]): string {
  return plots.find(isScannable)?.id ?? '';
}

export function reconcileScanLeader(plots: PlotSpec[], leaderId: string): string {
  return plots.some((plot) => plot.id === leaderId && isScannable(plot))
    ? leaderId
    : firstScanLeader(plots);
}

export function scanTargets(
  workspace: WorkspaceState,
  leaderId: string,
  navigationByPanel: Record<string, PanelNavigation>
): { previous: number | null; next: number | null } {
  const leader = workspace.plots.find((plot) => plot.id === leaderId);
  if (!leader || !isScannable(leader)) return { previous: null, next: null };
  const navigation = navigationByPanel[leaderId];
  if (navigation) return { previous: navigation.previous, next: navigation.next };
  if (plotByKind(leader.kind).timeDomain !== 'day') return { previous: null, next: null };
  const cursor = !leader.linkedTime && leader.cursorOverride != null ? leader.cursorOverride : workspace.cursor;
  const range = !leader.linkedTime && leader.rangeOverride ? leader.rangeOverride : workspace.range;
  return {
    previous: cursor - 86_400 >= range[0] ? cursor - 86_400 : null,
    next: cursor + 86_400 <= range[1] ? cursor + 86_400 : null
  };
}

/** A scan leader always advances shared time and its own independent cursor. */
export function applyScanCursor(workspace: WorkspaceState, leaderId: string, timestamp: number): WorkspaceState {
  const cursor = clampTimestamp(timestamp, workspace.range);
  return {
    ...workspace,
    cursor,
    plots: workspace.plots.map((plot) => plot.id === leaderId && !plot.linkedTime
      ? { ...plot, cursorOverride: clampTimestamp(timestamp, plot.rangeOverride ?? workspace.range) }
      : plot)
  };
}
